import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// Always use relative path for API - works in all environments
const API = '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(null); // Store impersonated user

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, role = 'contractor') => {
    const response = await axios.post(`${API}/auth/register`, { 
      email, 
      password, 
      name, 
      role 
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setImpersonating(null);
  };

  const isAdmin = () => user?.role === 'admin';

  // Impersonation functions
  const startImpersonating = (targetUser) => {
    if (user?.role === 'admin') {
      setImpersonating(targetUser);
    }
  };

  const stopImpersonating = () => {
    setImpersonating(null);
  };

  // Get the effective user (impersonated or real)
  const effectiveUser = impersonating || user;

  return (
    <AuthContext.Provider value={{ 
      user, 
      effectiveUser,
      token, 
      loading, 
      login, 
      register, 
      logout, 
      isAdmin,
      impersonating,
      startImpersonating,
      stopImpersonating
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
